import { FC, useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FLOATING_AGENT_ON_BRAND_COLOR } from "./floating-agent-window-chrome";
import { FloatingAgentVoice } from "./floating-agent-voice";
import {
  CIRCLE_DIAMETER_PX,
  CIRCLE_THINKING_ANIMATION_MS,
  CIRCLE_THINKING_REPLAY_COOLDOWN_MS,
  CIRCLE_WINDOW_SIZE_PX,
  CLICK_MAX_DURATION_MS,
  CLICK_VS_DRAG_THRESHOLD_PX,
  RING_BOX_PX,
} from "./floating-agent-constants";
import { DotLottieReact, type DotLottie } from "@lottiefiles/dotlottie-react";
import loadingArrowsAnimation from "@/assets/images/loading-arrows-tomato.json";
import { logger } from "@/modules/logger";

/** Duration of the release "sent" flash — matches hackweek's voicePeak. */
const VOICE_PEAK_MS = 480;

/**
 * The desktop window-move IPC ends at a native API taking 32-bit ints, which
 * rejects anything else with a "conversion failure" that crashes the main
 * process. Interrupted gestures (pointercancel, synthetic events) can carry
 * non-finite screenX/screenY, and dragging far past a screen edge can carry a
 * finite but enormous one — never send either.
 */
const MAX_SCREEN_COORD = 2 ** 31 - 1;

const isSendableScreenCoord = (value: number): boolean =>
  Number.isFinite(value) && Math.abs(value) <= MAX_SCREEN_COORD;

const hasFiniteScreenCoords = (e: React.PointerEvent<HTMLButtonElement>): boolean =>
  isSendableScreenCoord(e.screenX) && isSendableScreenCoord(e.screenY);

/**
 * Plays the arrows for one animation length every cooldown while thinking.
 *
 * Voice drives `.circle-mark` to opacity 0, so playback is gated on the mark
 * being visible — otherwise a burst runs out unseen.
 */
const useThinkingArrowsPlayback = (canPlay: boolean): boolean => {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!canPlay) {
      setIsPlaying(false);
      return;
    }

    let stopTimer: ReturnType<typeof setTimeout>;
    const play = () => {
      setIsPlaying(true);
      stopTimer = setTimeout(() => setIsPlaying(false), CIRCLE_THINKING_ANIMATION_MS);
    };

    play();
    const interval = setInterval(play, CIRCLE_THINKING_REPLAY_COOLDOWN_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(stopTimer);
    };
  }, [canPlay]);

  return isPlaying;
};

/**
 * The mark: one always-mounted player parked on frame 0 — the logo at rest —
 * that plays while thinking. DotLottieReact directly rather than
 * MDSLoadingArrows, which has no stop-and-rewind.
 */
const ThinkingArrows: FC<{ isPlaying: boolean }> = ({ isPlaying }) => {
  const dotLottieRef = useRef<DotLottie | null>(null);

  const handleDotLottieRef = useCallback((dotLottie: DotLottie | null) => {
    dotLottieRef.current = dotLottie;
  }, []);

  useEffect(() => {
    const dotLottie = dotLottieRef.current;
    if (!dotLottie) return;

    if (isPlaying) {
      dotLottie.play();
      return;
    }

    // stop() rewinds to frame 0; pause() would rest on an arbitrary frame.
    dotLottie.stop();
  }, [isPlaying]);

  return (
    <StyledThinkingArrows
      data={loadingArrowsAnimation}
      loop
      autoplay={false}
      dotLottieRefCallback={handleDotLottieRef}
    />
  );
};

export const FloatingAgentCircle: FC<{
  voice: FloatingAgentVoice;
  showSendConfirmation: boolean;
  isResizingShrink: boolean;
  isDismissing?: boolean;
  hasUnread: boolean;
  isThinking?: boolean;
  isOnboardingNudging?: boolean;
  onExpand: () => void;
  onHoverChange: (hovering: boolean) => void;
  /** Immediate tooltip teardown — the graced hover path is too slow for drags. */
  onDragStart: () => void;
  /**
   * OS-window drag, desktop only. When absent (web overlay: fixed position),
   * pointer movement never becomes a drag and the press stays a tap.
   */
  onDragMove?: (cursor: { cursorScreenX: number; cursorScreenY: number }) => void;
  onDragEnd?: (cursor: { cursorScreenX: number; cursorScreenY: number }) => void;
  onDismissAnimationEnd?: () => void;
}> = observer(
  ({
    voice,
    showSendConfirmation,
    isResizingShrink,
    isDismissing = false,
    hasUnread,
    isThinking = false,
    isOnboardingNudging = false,
    onExpand,
    onHoverChange,
    onDragStart,
    onDragMove,
    onDragEnd,
    onDismissAnimationEnd,
  }) => {
    const [isPeaking, setIsPeaking] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const wasListeningRef = useRef(false);

    // A listening → not-listening transition fires the one-shot release burst.
    useEffect(() => {
      if (wasListeningRef.current && !voice.isListening) {
        setIsPeaking(true);
        const timer = setTimeout(() => setIsPeaking(false), VOICE_PEAK_MS);
        return () => clearTimeout(timer);
      }
      wasListeningRef.current = voice.isListening;
    }, [voice.isListening]);

    const pointerStateRef = useRef<{
      pointerId: number;
      startX: number;
      startY: number;
      startedAtMs: number;
      dragging: boolean;
    } | null>(null);
    // Expanding on pointerup orphans the tap's synthetic click: the fullscreen
    // panel replaces the circle under the finger before the click dispatches,
    // so it hit-tests the freshly mounted composer and focuses it (pops the
    // keyboard on phones). Arm here, expand on the button's own click instead.
    const expandOnClickRef = useRef(false);

    const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      expandOnClickRef.current = false;
      pointerStateRef.current = {
        pointerId: e.pointerId,
        startX: e.screenX,
        startY: e.screenY,
        startedAtMs: performance.now(),
        dragging: false,
      };
    }, []);

    // The browser can revoke a captured pointer mid-gesture (focus loss,
    // gesture interruption); an in-progress drag must still end — otherwise
    // the drag visual sticks and the desktop window-drag never snaps.
    const handlePointerCancel = useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        const state = pointerStateRef.current;
        if (!state || state.pointerId !== e.pointerId) return;
        pointerStateRef.current = null;
        if (state.dragging) {
          // endedBy "pointermove" means the lost-pointerup recovery path.
          logger.warn({
            message: "[FloatingAgentCircle] drag ended abnormally",
            info: {
              endedBy: e.type,
              durationMs: Math.round(performance.now() - state.startedAtMs),
            },
          });
          setIsDragging(false);
          if (hasFiniteScreenCoords(e)) {
            onDragEnd?.({
              cursorScreenX: e.screenX,
              cursorScreenY: e.screenY,
            });
          }
        }
      },
      [onDragEnd]
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        const state = pointerStateRef.current;
        if (!state || state.pointerId !== e.pointerId) return;

        // A pointerup can be lost while the window moves under a captured
        // pointer; a move without a held button means the gesture is over.
        if (e.buttons === 0) {
          handlePointerCancel(e);
          return;
        }

        if (!onDragMove) return;
        if (!hasFiniteScreenCoords(e)) return;

        if (!state.dragging) {
          const movedPastThreshold =
            Math.abs(e.screenX - state.startX) > CLICK_VS_DRAG_THRESHOLD_PX ||
            Math.abs(e.screenY - state.startY) > CLICK_VS_DRAG_THRESHOLD_PX;
          if (!movedPastThreshold) return;
          state.dragging = true;
          setIsDragging(true);
          // A tooltip-grown window must shrink back before the drag proceeds —
          // drag-move geometry assumes the circle window.
          onDragStart();
        }

        onDragMove({
          cursorScreenX: e.screenX,
          cursorScreenY: e.screenY,
        });
      },
      [handlePointerCancel, onDragStart, onDragMove]
    );

    const handlePointerUp = useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        const state = pointerStateRef.current;
        pointerStateRef.current = null;
        if (!state || state.pointerId !== e.pointerId) return;

        if (state.dragging) {
          setIsDragging(false);
          if (hasFiniteScreenCoords(e)) {
            onDragEnd?.({
              cursorScreenX: e.screenX,
              cursorScreenY: e.screenY,
            });
          }
          return;
        }

        const heldMs = performance.now() - state.startedAtMs;
        if (heldMs <= CLICK_MAX_DURATION_MS) {
          expandOnClickRef.current = true;
        }
      },
      [onDragEnd]
    );

    const handleClick = useCallback(() => {
      if (!expandOnClickRef.current) return;
      expandOnClickRef.current = false;
      onExpand();
    }, [onExpand]);

    // Web has no window to drag (fixed overlay), so the circle is a plain
    // click target — a grab cursor there implies a drag that never happens.
    const isDraggable = onDragMove != null;

    const isStarting = voice.state === "pending" || voice.state === "starting";
    // Each of these drives `.circle-mark` to opacity 0.
    const isMarkVisible =
      !isStarting && !voice.isListening && !showSendConfirmation && !isDismissing;
    const isPlayingThinkingArrows = useThinkingArrowsPlayback(isThinking && isMarkVisible);

    return (
      <StyledCircleViewport
        style={{ "--voice-level": voice.amplitude } as React.CSSProperties}
        $isStarting={isStarting}
        $isListening={voice.isListening}
        $isPeaking={isPeaking}
        $isDragging={isDragging}
        $isConfirming={showSendConfirmation}
        $isResizingShrink={isResizingShrink}
        $isDismissing={isDismissing}
        $isOnboardingNudging={isOnboardingNudging}
        onAnimationEnd={event => {
          if (
            event.target === event.currentTarget &&
            event.animationName === "floating-agent-dismiss"
          ) {
            onDismissAnimationEnd?.();
          }
        }}
      >
        <StyledHalo className="halo" aria-hidden="true" />
        <StyledListeningRing className="voice-ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="ring-track" cx="50" cy="50" r="46" />
          <circle className="ring-sweep" cx="50" cy="50" r="46" />
        </StyledListeningRing>
        <StyledCircleButton
          className="circle"
          aria-label="Open Mem"
          data-floating-agent-hit-target="true"
          $isDraggable={isDraggable}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onPointerEnter={() => onHoverChange(true)}
          onPointerLeave={() => onHoverChange(false)}
        >
          {hasUnread && <StyledUnreadDot className="unread-dot" aria-label="Unread message" />}
          <StyledCircleMark aria-hidden="true" className="circle-mark">
            <ThinkingArrows isPlaying={isPlayingThinkingArrows} />
          </StyledCircleMark>
          <StyledCircleCheckmark
            className="circle-checkmark"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <polyline
              points="5 12.5 10 17.5 19 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </StyledCircleCheckmark>
          <StyledCircleVoiceBars aria-hidden="true" className="voice-bars">
            {voice.bars.map((bar, index) => (
              <span
                key={index}
                style={{ "--bar-level": bar } as React.CSSProperties}
                data-band={index}
              />
            ))}
          </StyledCircleVoiceBars>
        </StyledCircleButton>
      </StyledCircleViewport>
    );
  }
);

const StyledCircleViewport = styled.div<{
  $isStarting: boolean;
  $isListening: boolean;
  $isPeaking: boolean;
  $isDragging: boolean;
  $isConfirming: boolean;
  $isResizingShrink: boolean;
  $isDismissing: boolean;
  $isOnboardingNudging: boolean;
}>`
  position: relative;
  width: ${CIRCLE_WINDOW_SIZE_PX}px;
  height: ${CIRCLE_WINDOW_SIZE_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;

  /* The sweep is a partial stroke rotating around the perimeter while the
     talk key is held; the voice bars carry the amplitude motion. On release,
     a peak burst plays a single outward "sent" flash. */
  .ring-track,
  .ring-sweep {
    fill: none;
  }

  .ring-track {
    stroke: ${({ theme }) => theme.colors.border.default};
    stroke-width: 2;
    opacity: 0.4;
  }

  /* Dash + gap sum to the r=46 circumference (2π×46 ≈ 289) so one keyframe
     period maps the pattern onto itself — the sweep chases its own tail with
     no dead zone. (Hackweek's 70/360 gap exceeded the circumference, making
     the dash vanish briefly each cycle.) */
  /* Identical to the boot-loading sweep (floating-agent-window-chrome.tsx):
     constant 3px stroke, no glow — the bars carry the amplitude motion. */
  .ring-sweep {
    stroke: ${({ theme }) => theme.colors.text.link.default};
    stroke-width: 3;
    stroke-linecap: round;
    stroke-dasharray: 70 219;
    transform-origin: center;
    transform: rotate(-90deg);
    opacity: 0;
  }

  /* Keydown feedback, before the mic is open: blue circle with the bars at
     rest (levels are still 0), no ring — the ring + start sound mark the mic
     actually going live. */
  ${({ $isStarting, theme }) =>
    $isStarting &&
    `
    .circle {
      background: ${theme.colors.icon.emphasis};
    }
    .circle-mark {
      opacity: 0;
    }
    .voice-bars {
      display: flex;
    }
  `}

  ${({ $isListening, theme }) =>
    $isListening &&
    `
    /* Listening turns the circle to the emphasis blue with white bars (the
       sweep + amplitude motion is unchanged). */
    .circle {
      background: ${theme.colors.icon.emphasis};
    }
    .voice-ring {
      opacity: 1;
    }
    .ring-sweep {
      opacity: 0.8;
      animation: floating-agent-voice-sweep 1.4s linear infinite;
    }
    .circle-mark {
      opacity: 0;
    }
    .voice-bars {
      display: flex;
    }
  `}

  ${({ $isPeaking }) =>
    $isPeaking &&
    `
    .voice-ring {
      opacity: 1;
    }
    .ring-sweep {
      animation: floating-agent-voice-peak ${VOICE_PEAK_MS}ms ease-out forwards;
      stroke-dasharray: 290 0;
    }
  `}

  ${({ $isDragging }) =>
    $isDragging &&
    `
    .circle {
      transform: scale(1.08);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
    }
    .halo {
      opacity: 0.7;
      transform: scale(1.08);
    }
  `}

  ${({ $isConfirming }) =>
    $isConfirming &&
    `
    .circle-checkmark {
      opacity: 1;
      transition: opacity 140ms ease 80ms;
    }
    .circle-checkmark polyline {
      stroke-dashoffset: 0;
      transition: stroke-dashoffset 360ms cubic-bezier(0.16, 1, 0.3, 1) 100ms;
    }
    .circle-mark {
      opacity: 0;
      transition: opacity 140ms ease;
    }
  `}

  ${({ $isOnboardingNudging }) =>
    $isOnboardingNudging &&
    `
    .circle {
      animation: floating-agent-onboarding-nudge 720ms cubic-bezier(0.34, 1.56, 0.64, 1)
        infinite;
      box-shadow: 0 5px 18px rgba(0, 0, 0, 0.24);
    }
    .circle-mark {
      animation: floating-agent-onboarding-mark-counter-nudge 720ms cubic-bezier(0.34, 1.56, 0.64, 1)
        infinite;
    }
    .halo {
      opacity: 0.72;
      transform: scale(1.1);
    }
  `}

  /* Hide the chrome for the shrink frame — the OS animates window bounds and
     visible chrome would ghost at the old position. Must be instant. */
  ${({ $isResizingShrink }) =>
    $isResizingShrink &&
    `
    .circle,
    .halo,
    .voice-ring {
      opacity: 0;
      transition: none;
    }
  `}

  /* Dismissal is a terminal visual state. Confirmation ending would normally
     cross-fade the checkmark back to the Mem mark, while release/onboarding
     animations can still be running. Fade every adornment for the first
     80ms, then shrink only the stable tomato circle for the remaining 180ms. */
  ${({ $isDismissing, theme }) =>
    $isDismissing &&
    `
    pointer-events: none;
    animation: floating-agent-dismiss 260ms linear forwards;

    .circle,
    .circle:hover {
      animation: none;
      background: ${theme.colors.icon.brand};
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.18);
      transform: none;
      transition: none;
    }
    .circle-mark,
    .circle-checkmark,
    .voice-bars,
    .unread-dot,
    .halo,
    .voice-ring {
      opacity: 0;
      animation: none;
      transition: opacity 80ms ease-out;
    }
    .circle-mark,
    .circle-checkmark polyline,
    .ring-sweep {
      animation: none;
    }
  `}

  @keyframes floating-agent-voice-sweep {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: -289;
    }
  }

  @keyframes floating-agent-voice-peak {
    0% {
      opacity: 1;
      stroke-width: 5px;
      transform: rotate(-90deg) scale(1);
    }
    100% {
      opacity: 0;
      stroke-width: 1px;
      /* A touch of expansion past the listening ring, small enough to stay
         inside the 80px window through the fade-out. */
      transform: rotate(-90deg) scale(1.2);
    }
  }

  @keyframes floating-agent-dismiss {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    31% {
      opacity: 1;
      transform: scale(1);
      animation-timing-function: cubic-bezier(0.4, 0, 1, 1);
    }
    100% {
      opacity: 0;
      transform: scale(0.62);
    }
  }

  @keyframes floating-agent-onboarding-nudge {
    0%,
    100% {
      transform: scale(1);
    }
    45% {
      transform: scale(1.1);
    }
  }

  @keyframes floating-agent-onboarding-mark-counter-nudge {
    0%,
    100% {
      transform: scale(1);
    }
    45% {
      transform: scale(calc(1 / 1.1));
    }
  }
`;

/* Hackweek's circle+16px ring box, centered in the (larger) window so the
   glow and release burst have clearance to the window edge. */
const StyledListeningRing = styled.svg`
  position: absolute;
  inset: ${(CIRCLE_WINDOW_SIZE_PX - RING_BOX_PX) / 2}px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 120ms ease;
  overflow: visible;
`;

const StyledCircleVoiceBars = styled.div`
  display: none;
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;

  span {
    display: block;
    width: 4px;
    min-height: 3px;
    border-radius: 2px;
    background: ${FLOATING_AGENT_ON_BRAND_COLOR};
    transition: height 70ms ease-out;
  }

  span[data-band="0"],
  span[data-band="4"] {
    height: calc(12% + 40% * var(--bar-level, 0));
  }
  span[data-band="1"],
  span[data-band="3"] {
    height: calc(12% + 44% * var(--bar-level, 0));
  }
  span[data-band="2"] {
    height: calc(12% + 48% * var(--bar-level, 0));
  }
`;

/* Hackweek's halo/pulse used the brand tomato — editor.400 in MDS foundations
   — so the glow keeps that color under the MDS-authoritative rule. */
const HALO_RGB = "255, 54, 26";

/* Centered on the circle's 45° top-right edge: the 64px button has its edge
   point at (54.6, 9.4); an 8px half-dot inset lands the center on the rim. */
const StyledUnreadDot = styled.span`
  position: absolute;
  top: 1px;
  right: 1px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.icon.emphasis};
`;

/* No blur filter: on the transparent widget window the filter promotes a
   composited layer whose rasterized bounds render as a faint rectangle. The
   radial gradient is soft enough on its own. */
const StyledHalo = styled.div`
  position: absolute;
  width: ${CIRCLE_DIAMETER_PX + 27}px;
  height: ${CIRCLE_DIAMETER_PX + 27}px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(${HALO_RGB}, 0.16) 0%,
    rgba(${HALO_RGB}, 0.07) 40%,
    rgba(${HALO_RGB}, 0) 68%
  );
  pointer-events: none;
  opacity: 0.45;
  transition:
    opacity 220ms ease,
    transform 220ms ease;
`;

const StyledCircleButton = styled.button<{ $isDraggable: boolean }>`
  position: relative;
  width: ${CIRCLE_DIAMETER_PX}px;
  height: ${CIRCLE_DIAMETER_PX}px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.icon.brand};
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isDraggable }) => ($isDraggable ? "grab" : "pointer")};
  pointer-events: auto;
  padding: 0;
  -webkit-app-region: no-drag;
  transition: background 180ms ease;

  &:active {
    cursor: ${({ $isDraggable }) => ($isDraggable ? "grabbing" : "pointer")};
  }

  /* Backout cubic-bezier — accelerates fast, overshoots ~6% past the target
     scale, then settles: a subtle "standing at attention" bounce on hover. */
  &:hover {
    transform: scale(1.06);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
    transition:
      transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 180ms ease;
  }

  /* Counter-scale the mark by the inverse of the hover scale so the logo
     stays visually fixed while the circle bounces around it. */
  &:hover .circle-mark {
    transform: scale(calc(1 / 1.06));
    transition: transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }
`;

const StyledCircleMark = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${FLOATING_AGENT_ON_BRAND_COLOR};
  pointer-events: none;
  user-select: none;
  transition:
    opacity 120ms ease,
    transform 240ms cubic-bezier(0.16, 1, 0.3, 1);
`;

/* The artboard reserves sweep margin around the glyph, so the mark reads small
   at its natural box size — the scale is calibrated on screen. The filter
   repaints the tomato asset white against the tomato circle. */
const THINKING_ARROWS_BOX_PX = 48;
const THINKING_ARROWS_SCALE = 1.27;

const StyledThinkingArrows = styled(DotLottieReact)`
  width: ${THINKING_ARROWS_BOX_PX}px;
  height: ${THINKING_ARROWS_BOX_PX}px;
  transform: scale(${THINKING_ARROWS_SCALE});
  filter: brightness(0) invert(1);
`;

const StyledCircleCheckmark = styled.svg`
  position: absolute;
  inset: 0;
  width: 50%;
  height: 50%;
  margin: auto;
  color: ${FLOATING_AGENT_ON_BRAND_COLOR};
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms ease;

  polyline {
    stroke-dasharray: 30;
    stroke-dashoffset: 30;
    transition: stroke-dashoffset 320ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`;
